import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';
import { AvatarService, AvatarRequest, AvatarName } from '../../../services/avatar.service';
import { finalize } from 'rxjs/operators';

type AvatarVariant = 'original' | 'green' | 'video';

interface AvatarAssetConfig {
  original?: string;
  green?: string;
  video?: string;
}

const AVATAR_ASSET_MAP: Record<string, AvatarAssetConfig> = {
  'ÖZLEM ÖZEN': {
    original: 'ÖZLEM ÖZEN.png',
    green: 'ÖZLEM ÖZEN-green.png'
  },
  'MEHMET ALİ ÖZ': {
    original: 'MEHMET ALİ ÖZ.png',
    green: 'MEHMET ALİ ÖZ-green.png',
    video: 'MEHMET ALİ ÖZ DEMO VİDEO.mp4'
  },
  'IRAZ ÖNDER': {
    original: 'IRAZ ÖNDER.png',
    green: 'IRAZ ÖNDER-green.png'
  },
  'HASAN CAN GÖKSOY': {
    original: 'HASAN CAN GÖKSOY.png',
    green: 'HASAN CAN GÖKSOY-green.png'
  },
  'BURAK ALTAN': {
    original: 'BURAK ALTAN.png',
    green: 'BURAK ALTAN-green.png'
  },
  'AYTUĞ YÜKSEL': {
    original: 'AYTUĞ YÜKSEL.png',
    green: 'AYTUĞ YÜKSEL-green.png'
  },
  'AHMET ÖZ': {
    original: 'AHMET ÖZ.png',
    green: 'AHMET ÖZ-green.png'
  }
};

@Component({
  selector: 'app-avatar',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.css']
})
export class AvatarComponent implements OnInit {
  avatarForm: FormGroup;
  avatarRequests: AvatarRequest[] = [];
  uploadedVideos: AvatarRequest[] = [];
  avatarNames: AvatarName[] = [];
  pendingRequests: AvatarRequest[] = []; // For superuser dropdown
  selectedFile: File | null = null;
  selectedVideoFile: File | null = null;
  selectedRequestId: number | null = null;
  uploadProgress: number = 0;
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  currentPage: number = 1;
  totalPages: number = 1;
  selectedStatus: string = '';
  activeTab: 'new-video' | 'my-videos' = 'new-video';
  userRole: string | null = null;
  isSuperUser: boolean = false;
  downloadingVideoIds: Set<number> = new Set();
  downloadProgress: { [id: number]: number | null } = {};
  
  // Transcript modal
  showTranscriptModal: boolean = false;
  transcriptContent: string = '';
  transcriptFileName: string = '';
  transcriptType: string = '';
  transcriptDownloadUrl: string = '';
  currentTranscriptRequest: AvatarRequest | null = null;
  
  statusOptions = [
    { value: '', label: 'Tümü' },
    { value: 'pending', label: 'Beklemede' },
    { value: 'in_progress', label: 'İşleniyor' },
    { value: 'completed', label: 'Tamamlandı' },
    { value: 'delivered', label: 'Teslim Edildi' }
  ];

  constructor(
    private fb: FormBuilder,
    private avatarService: AvatarService
  ) {
    this.avatarForm = this.fb.group({
      avatar_name: ['', Validators.required],
      notes: ['']
    });
  }

  selectedAvatarName: string | null = null;
  selectedVariant: AvatarVariant = 'original';

  avatarVariantOptions: { value: AvatarVariant; label: string }[] = [
    { value: 'original', label: 'Orijinal' },
    { value: 'green', label: 'Green' },
    { value: 'video', label: 'Video' }
  ];

  private readonly assetBasePath = 'assets/avatars';

  ngOnInit(): void {
    this.userRole = localStorage.getItem('userRole');
    this.isSuperUser = this.userRole === 'superuser';
    this.loadAvatarNames();
    this.loadAvatarRequests();
    if (this.isSuperUser) {
      this.loadUploadedVideos();
      this.loadPendingRequests();
    }

    const avatarNameControl = this.avatarForm.get('avatar_name');
    avatarNameControl?.valueChanges.subscribe((value: string | null) => {
      this.selectedAvatarName = value || null;
      this.selectedVariant = this.getDefaultVariantFor(this.selectedAvatarName);
    });
  }

  loadAvatarNames(): void {
    this.avatarService.getAvatarNames().subscribe({
      next: (response) => {
        this.avatarNames = response.data;
      },
      error: (error) => {
        console.error('Error loading avatar names:', error);
        this.showError('Avatar isimleri yüklenirken hata oluştu');
      }
    });
  }


  loadPendingRequests(): void {
    if (!this.isSuperUser) return;
    
    this.avatarService.getAvatarRequests('pending', 1, 100).subscribe({
      next: (response) => {
        this.pendingRequests = response.data;
      },
      error: (error) => {
        console.error('Error loading pending requests:', error);
      }
    });
  }

  loadUploadedVideos(): void {
    if (!this.isSuperUser) return;
    
    this.avatarService.getUploadedVideos().subscribe({
      next: (response) => {
        this.uploadedVideos = response.data;
      },
      error: (error) => {
        console.error('Error loading uploaded videos:', error);
        this.showError('Yüklenen videolar alınırken hata oluştu');
      }
    });
  }

  switchTab(tab: 'new-video' | 'my-videos'): void {
    this.activeTab = tab;
    this.currentPage = 1; // Reset to first page when switching tabs
    if (tab === 'my-videos') {
      this.loadCompletedVideos();
    } else {
      this.loadAvatarRequests();
    }
  }
  
  loadCompletedVideos(): void {
    this.isLoading = true;
    // Load all requests without status filter to get all completed videos
    // We'll filter on client side to show only completed/delivered with video files
    this.avatarService.getAvatarRequests('', this.currentPage, 100).subscribe({
      next: (response) => {
        // Filter only completed/delivered videos that have video files
        const filtered = response.data.filter(req => 
          (req.status === 'completed' || req.status === 'delivered') && req.video_file_path
        );
        this.uploadedVideos = filtered;
        // Calculate total pages based on filtered results
        // Since we're loading all and filtering, we need to estimate pages
        this.totalPages = Math.max(1, Math.ceil(response.pagination.total / 100));
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading completed videos:', error);
        this.showError('Tamamlanan videolar yüklenirken hata oluştu');
        this.isLoading = false;
      }
    });
  }
  
  loadAvatarRequests(statusFilter?: string): void {
    this.isLoading = true;
    const status = statusFilter || this.selectedStatus;
    this.avatarService.getAvatarRequests(status, this.currentPage).subscribe({
      next: (response) => {
        this.avatarRequests = response.data;
        this.totalPages = response.pagination.totalPages;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading avatar requests:', error);
        this.showError('Avatar talepleri yüklenirken hata oluştu');
        this.isLoading = false;
      }
    });
  }

  onVideoFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
      if (allowedTypes.includes(file.type)) {
        this.selectedVideoFile = file;
      } else {
        this.showError('Sadece video dosyaları kabul edilir (MP4, MPEG, MOV, AVI, WebM)');
        event.target.value = '';
      }
    }
  }

  uploadManualVideo(): void {
    if (!this.selectedVideoFile) {
      this.showError('Lütfen bir video dosyası seçin');
      return;
    }

    // Superuser must select a request if available
    if (this.isSuperUser && this.pendingRequests.length > 0 && !this.selectedRequestId) {
      this.showError('Lütfen bir transcript talebi seçin');
      return;
    }

    this.isLoading = true;
    this.avatarService.uploadManualVideo(this.selectedVideoFile, this.selectedRequestId || undefined).subscribe({
      next: (response) => {
        this.showSuccess('Video başarıyla yüklendi');
        this.selectedVideoFile = null;
        this.selectedRequestId = null;
        this.loadAvatarRequests(); // Reload all requests to show updated status
        this.loadPendingRequests(); // Reload pending requests
        this.loadUploadedVideos();
        this.isLoading = false;
        
        const fileInput = document.getElementById('videoFile') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      },
      error: (error) => {
        console.error('Error uploading video:', error);
        this.showError(error.error?.message || 'Video yüklenirken hata oluştu');
        this.isLoading = false;
      }
    });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['text/plain', 'application/pdf', 'application/msword', 
                           'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (allowedTypes.includes(file.type)) {
        this.selectedFile = file;
      } else {
        this.showError('Sadece text, PDF ve Word dosyaları kabul edilir');
        event.target.value = '';
      }
    }
  }

  onSubmit(): void {
    if (this.avatarForm.valid && this.selectedFile) {
      const formValue = this.avatarForm.value;
      this.isLoading = true;
      
      this.avatarService.createAvatarRequest(
        formValue.avatar_name,
        this.selectedFile,
        formValue.notes
      ).subscribe({
        next: (response) => {
          this.showSuccess('Avatar talebi başarıyla oluşturuldu. Ekibimize bildirim gönderildi.');
          this.avatarForm.reset();
          this.selectedFile = null;
          this.loadAvatarRequests();
          this.isLoading = false;
          
          this.selectedAvatarName = null;
          this.selectedVariant = 'original';

          const fileInput = document.getElementById('transcript') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
        },
        error: (error) => {
          console.error('Error creating avatar request:', error);
          this.showError('Avatar talebi oluşturulurken hata oluştu');
          this.isLoading = false;
        }
      });
    } else {
      this.showError('Lütfen tüm gerekli alanları doldurun ve bir dosya seçin');
    }
  }

  downloadVideo(request: AvatarRequest): void {
    const requestId = request.id;
    if (!request.video_file_path || !requestId) {
      return;
    }

    if (this.downloadingVideoIds.has(requestId)) {
      return;
    }

    this.downloadingVideoIds.add(requestId);
    this.downloadProgress[requestId] = 0;

    this.avatarService.downloadAvatarVideo(requestId).pipe(
      finalize(() => {
        this.downloadingVideoIds.delete(requestId);
        delete this.downloadProgress[requestId];
      })
    ).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.DownloadProgress) {
          if (event.total) {
            this.downloadProgress[requestId] = Math.round(100 * (event.loaded / event.total));
          } else {
            this.downloadProgress[requestId] = null;
          }
        } else if (event.type === HttpEventType.Response) {
          this.downloadProgress[requestId] = 100;
          const blob = event.body as Blob;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `avatar_${request.avatar_name}_${requestId}.mp4`;
          link.click();
          window.URL.revokeObjectURL(url);
          
          this.showSuccess('Video başarıyla indirildi');
        }
      },
      error: (error) => {
        console.error('Error downloading video:', error);
        this.showError('Video indirilemedi');
      }
    });
  }

  isDownloading(id?: number | null): boolean {
    if (!id) {
      return false;
    }
    return this.downloadingVideoIds.has(id);
  }

  getDownloadProgress(id?: number | null): number | null {
    if (!id) {
      return null;
    }
    const progress = this.downloadProgress[id];
    return typeof progress === 'number' ? progress : null;
  }

  formatDownloadProgress(id?: number | null): string {
    const progress = this.getDownloadProgress(id);
    if (progress === null) {
      return 'Hazırlanıyor...';
    }
    return `${progress}%`;
  }

  downloadTranscript(request: AvatarRequest): void {
    if (request.transcript_file_path && request.id) {
      this.avatarService.downloadTranscript(request.id).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const fileName = request.transcript_file_path?.split('/').pop() || `transcript_${request.id}`;
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(url);
          
          this.showSuccess('Transcript başarıyla indirildi');
        },
        error: (error) => {
          console.error('Error downloading transcript:', error);
          this.showError('Transcript indirilemedi');
        }
      });
    }
  }

  viewTranscript(request: AvatarRequest): void {
    if (!request.transcript_file_path || !request.id) {
      this.showError('Transcript dosyası bulunamadı');
      return;
    }

    this.currentTranscriptRequest = request;
    this.isLoading = true;
    
    this.avatarService.getTranscriptContent(request.id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.transcriptFileName = response.data.fileName;
          this.transcriptType = response.data.type;
          
          if (response.data.type === 'text' && response.data.content) {
            // Text file - show content in modal
            this.transcriptContent = response.data.content;
            this.showTranscriptModal = true;
          } else if (response.data.type === 'pdf' || response.data.type === 'word') {
            // PDF or Word - show download option
            this.transcriptDownloadUrl = response.data.downloadUrl || '';
            this.transcriptContent = '';
            this.showTranscriptModal = true;
          }
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading transcript content:', error);
        this.showError('Transcript içeriği yüklenemedi');
        this.isLoading = false;
      }
    });
  }

  closeTranscriptModal(): void {
    this.showTranscriptModal = false;
    this.transcriptContent = '';
    this.transcriptFileName = '';
    this.transcriptType = '';
    this.transcriptDownloadUrl = '';
    this.currentTranscriptRequest = null;
  }

  deleteRequest(request: AvatarRequest): void {
    if (confirm(`${request.avatar_name} avatar talebini silmek istediğinizden emin misiniz?`)) {
      this.avatarService.deleteAvatarRequest(request.id!).subscribe({
        next: () => {
          this.showSuccess('Avatar talebi başarıyla silindi');
          this.loadAvatarRequests();
        },
        error: (error) => {
          console.error('Error deleting request:', error);
          this.showError('Talep silinemedi');
        }
      });
    }
  }

  deleteUploadedVideo(video: AvatarRequest): void {
    if (confirm('Bu videoyu silmek istediğinizden emin misiniz?')) {
      this.avatarService.deleteUploadedVideo(video.id!).subscribe({
        next: () => {
          this.showSuccess('Video başarıyla silindi');
          this.loadUploadedVideos();
        },
        error: (error) => {
          console.error('Error deleting video:', error);
          this.showError('Video silinemedi');
        }
      });
    }
  }

  onStatusFilterChange(event: any): void {
    this.selectedStatus = event.target.value;
    this.currentPage = 1;
    this.loadAvatarRequests();
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadAvatarRequests();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadAvatarRequests();
    }
  }

  getStatusLabel(status: string): string {
    const option = this.statusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  }

  getStatusClass(status: string): string {
    switch(status) {
      case 'pending':
        return 'badge-warning';
      case 'in_progress':
        return 'badge-info';
      case 'completed':
        return 'badge-success';
      case 'delivered':
        return 'badge-primary';
      default:
        return 'badge-secondary';
    }
  }

  private showError(message: string): void {
    this.errorMessage = message;
    setTimeout(() => {
      this.errorMessage = '';
    }, 5000);
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }

  onVariantChange(variant: AvatarVariant): void {
    this.selectedVariant = variant;
  }

  isVariantAvailable(name: string | null, variant: AvatarVariant): boolean {
    if (!name) {
      return false;
    }
    const assetConfig = this.getAssetConfig(name);
    return Boolean(assetConfig[variant]);
  }

  isVideoSelected(): boolean {
    return this.selectedVariant === 'video';
  }

  get selectedMediaPath(): string | null {
    if (!this.selectedAvatarName) {
      return null;
    }
    return this.getVariantAssetPath(this.selectedAvatarName, this.selectedVariant);
  }

  private getVariantAssetPath(name: string, variant: AvatarVariant): string | null {
    const assetConfig = this.getAssetConfig(name);
    const fileName = assetConfig[variant];
    if (!fileName) {
      return null;
    }
    return encodeURI(`${this.assetBasePath}/${fileName}`);
  }

  private getAssetConfig(name: string): AvatarAssetConfig {
    const config = AVATAR_ASSET_MAP[name];
    if (config) {
      return config;
    }

    return {
      original: `${name}.png`
    };
  }

  private getDefaultVariantFor(name: string | null): AvatarVariant {
    if (!name) {
      return 'original';
    }

    const availableVariants = this.getAvailableVariants(name);
    if (availableVariants.includes('original')) {
      return 'original';
    }

    return availableVariants[0] || 'original';
  }

  private getAvailableVariants(name: string): AvatarVariant[] {
    const config = this.getAssetConfig(name);
    const variants: AvatarVariant[] = [];

    if (config.original) {
      variants.push('original');
    }
    if (config.green) {
      variants.push('green');
    }
    if (config.video) {
      variants.push('video');
    }

    return variants;
  }
}